import {useEffect, useState} from 'react';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import axios from 'axios';
import {Confirm, Notify} from 'notiflix';
import {useSelector} from 'react-redux';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import {ENDPOINT} from 'constants';
import {getRoleBadgeColor} from './moduleConfig';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const EMPTY_FORM = {name: '', modules: [], fullAccess: false};

const columns = [
  {Header: 'Role Name', accessor: 'name', align: 'left'},
  {Header: 'Modules', accessor: 'modules', align: 'left'},
  {Header: 'Type', accessor: 'type', align: 'center'},
  {Header: 'Actions', accessor: 'actions', align: 'center'},
];

// Settings > Account Management > Roles. A role only decides which modules
// are VISIBLE to the accounts assigned it — it does not grant any
// create/read/update/delete actions by itself. Those are assigned
// per-account instead (Settings > Account Management > Accounts), so two
// accounts sharing the same role (e.g. "Data Entry Operator") can still end
// up with different actions — one full CRUD, another read-only — on the
// exact same module. See src/constants/rolePermissions.js on the backend.
// SuperAdmin and Admin themselves are fixed "system roles" the backend never
// allows editing or deleting (403) — reflected here by disabling their row
// actions rather than letting the click round-trip fail.
const RolesTab = () => {
  const {user} = useSelector((state) => state.auth);
  const canRead = usePermission('account-management', 'read');
  const authHeaders = {headers: {Authorization: `Bearer ${user?.accessToken}`}};
  const rolesApi = `${ENDPOINT}api/v1/roles`;

  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]); // [{key, label, actions}]
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [rolesRes, modulesRes] = await Promise.all([
        axios.get(rolesApi, authHeaders),
        axios.get(`${rolesApi}/modules`, authHeaders),
      ]);
      setRoles(rolesRes.data?.data || []);
      setModules(modulesRes.data?.data || []);
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const setField = (patch) => setForm((prev) => ({...prev, ...patch}));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    if (role.isSystemRole) return;
    const isFullAccess = Array.isArray(role.permissions) && role.permissions.includes('*');
    setEditing(role);
    setForm({
      name: role.name,
      modules: isFullAccess ? [] : role.permissions || [],
      fullAccess: isFullAccess,
    });
    setDialogOpen(true);
  };

  const toggleModule = (moduleKey) => {
    setForm((prev) => ({
      ...prev,
      modules: prev.modules.includes(moduleKey)
        ? prev.modules.filter((m) => m !== moduleKey)
        : [...prev.modules, moduleKey],
    }));
  };

  const validate = () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      return 'Role name must be at least 2 characters';
    }
    if (!form.fullAccess && form.modules.length === 0) {
      return 'Select at least one module, or turn on Full Access';
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      Notify.warning(error);
      return;
    }
    const payload = {
      name: form.name,
      permissions: form.fullAccess ? ['*'] : form.modules,
    };
    try {
      setSaving(true);
      if (editing) {
        await axios.patch(`${rolesApi}/${editing.id}`, payload, authHeaders);
        Notify.success('Role updated');
      } else {
        await axios.post(rolesApi, payload, authHeaders);
        Notify.success('Role created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (role) => {
    if (role.isSystemRole) return;
    Confirm.show(
      'Delete role',
      `Delete the "${role.name}" role? Accounts must be reassigned to a different role first.`,
      'Delete',
      'Cancel',
      async () => {
        try {
          await axios.delete(
            `${rolesApi}/${role.id}?label=${encodeURIComponent(role.name)}`,
            authHeaders,
          );
          Notify.success('Role deleted');
          fetchAll();
        } catch (error) {
          Notify.failure(error?.response?.data?.message || 'Failed to delete role');
        }
      },
    );
  };

  const rows = roles.map((role) => {
    const isFullAccess = Array.isArray(role.permissions) && role.permissions.includes('*');
    const grantedModules = isFullAccess ? [] : role.permissions || [];

    return {
      name: (
        <MDTypography variant="button" fontWeight="medium">
          {role.name}
        </MDTypography>
      ),
      modules: isFullAccess ? (
        <MDBadge badgeContent="Full Access" color="error" variant="gradient" size="sm" />
      ) : (
        <MDBox display="flex" gap={0.5} flexWrap="wrap">
          {grantedModules.length === 0 && (
            <MDTypography variant="caption" color="text">
              No modules
            </MDTypography>
          )}
          {grantedModules.map((moduleKey) => {
            const moduleInfo = modules.find((m) => m.key === moduleKey);
            return (
              <MDBadge
                key={moduleKey}
                badgeContent={moduleInfo?.label || moduleKey}
                color="info"
                variant="contained"
                size="sm"
              />
            );
          })}
        </MDBox>
      ),
      type: role.isSystemRole ? (
        <MDBadge
          badgeContent="System role"
          color={getRoleBadgeColor(role)}
          variant="gradient"
          size="sm"
        />
      ) : (
        <MDTypography variant="caption" color="text">
          Custom
        </MDTypography>
      ),
      actions: (
        <MDBox display="flex" gap={1} justifyContent="center">
          <Tooltip title={role.isSystemRole ? 'System roles cannot be modified' : 'Edit'}>
            <span>
              <IconButton size="small" disabled={role.isSystemRole} onClick={() => openEdit(role)}>
                <Icon fontSize="small">edit</Icon>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={role.isSystemRole ? 'System roles cannot be modified' : 'Delete'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={role.isSystemRole}
                onClick={() => handleDelete(role)}>
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </MDBox>
      ),
    };
  });

  if (!canRead) {
    return (
      <MDBox>
        <NoPermissionNotice message="Your role doesn't include read access to Account Management. Ask an administrator to update your permissions." />
      </MDBox>
    );
  }

  return (
    <>
      <MDBox
        p={3}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}>
        <MDButton variant="gradient" color="info" onClick={openCreate}>
          <Icon sx={{mr: 0.5}}>add</Icon> Create New Role
        </MDButton>
      </MDBox>

      {loading ? (
        <MDBox py={5} textAlign="center">
          <MDTypography variant="button" color="text">
            Loading roles...
          </MDTypography>
        </MDBox>
      ) : (
        <DataTable
          table={{columns, rows}}
          isSorted={false}
          entriesPerPage={{defaultValue: 10, entries: [10, 25, 50]}}
          showTotalEntries
          noEndBorder
          canSearch
        />
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="sm">
        <DialogTitle>{editing ? 'Edit role' : 'Create new role'}</DialogTitle>
        <DialogContent dividers>
          <MDBox mb={2}>
            <TextField
              fullWidth
              label="Role Name"
              value={form.name}
              onChange={(e) => setField({name: e.target.value})}
            />
          </MDBox>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.fullAccess}
                onChange={(e) => setField({fullAccess: e.target.checked})}
              />
            }
            label="Full Access (*) — grants every module, including future ones"
          />
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            A role only controls which modules an account can SEE. The specific actions
            (create/read/update/delete) each account gets within those modules are set when that
            account is created or edited, in Account Management.
          </MDTypography>

          {!form.fullAccess && (
            <MDBox mt={1}>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                Modules
              </MDTypography>
              <FormGroup>
                <Grid container>
                  {modules.map((moduleInfo) => (
                    <Grid item xs={12} sm={6} key={moduleInfo.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.modules.includes(moduleInfo.key)}
                            onChange={() => toggleModule(moduleInfo.key)}
                          />
                        }
                        label={moduleInfo.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={saving}
            onClick={() => setDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Create role'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RolesTab;
